import { useState, useEffect } from "react";
import { useAuth } from "./AuthContext";

const API_BASE = "http://localhost:8000";

// ─── Mock data fallbacks (used when API returns nothing) ──────────────────────

const MOCK_DATA = {
  eal: 23500000,
  eal_previous: 28200000,
  maturity_scores: {
    identity_access: { score: 2, target: 4, label: "Identity & Access Mgmt" },
    network_security: { score: 3, target: 5, label: "Network Security" },
    data_protection: { score: 1, target: 4, label: "Data Protection" },
    incident_response: { score: 2, target: 4, label: "Incident Response" },
    vulnerability_management: { score: 3, target: 4, label: "Vulnerability Mgmt" },
    cloud_security: { score: 2, target: 4, label: "Cloud Security" },
    compliance_governance: { score: 2, target: 5, label: "Compliance & Governance" },
    logging_monitoring: { score: 1, target: 4, label: "Logging & Monitoring" },
  },
  remediation: { total: 68, complete: 12, in_progress: 8, blocked: 3 },
  critical_findings: [
    { title: "No MFA on privileged accounts", severity: "critical", category: "Identity & Access Mgmt" },
    { title: "Customer data not encrypted at rest", severity: "critical", category: "Data Protection" },
    { title: "CloudTrail logging disabled", severity: "critical", category: "Cloud Security" },
    { title: "No incident response plan documented", severity: "high", category: "Incident Response" },
    { title: "Patch SLAs undefined", severity: "high", category: "Vulnerability Mgmt" },
  ],
  frameworks: ["GLBA", "PCI-DSS", "FFIEC"],
  org_name: "Acme Financial Services",
  report_period: "Q1 2026",
};

// ─── Severity config ──────────────────────────────────────────────────────────

const SEV_CONFIG = {
  critical: { color: "#ef4444", bg: "#ef444415", label: "Critical" },
  high:     { color: "#f97316", bg: "#f9731615", label: "High" },
  medium:   { color: "#eab308", bg: "#eab30815", label: "Medium" },
  low:      { color: "#22c55e", bg: "#22c55e15", label: "Low" },
};

// ─── Data fetchers ────────────────────────────────────────────────────────────

async function fetchReportData(customerId) {
  const data = { ...MOCK_DATA };
  try {
    const asmRes = await fetch(`${API_BASE}/api/assessments/current?customer_id=${customerId}`);
    if (asmRes.ok) {
      const asmData = await asmRes.json();
      const responses = asmData.assessment?.responses || asmData.responses || {};
      if (Object.keys(responses).length > 0) {
        data.maturity_scores = responses;
        data.org_name = asmData.assessment?.organization_name || data.org_name;
      }
    }
  } catch (_) {}
  try {
    const riskRes = await fetch(`${API_BASE}/api/risk/latest?customer_id=${customerId}`);
    if (riskRes.ok) {
      const riskData = await riskRes.json();
      if (riskData?.results?.eal) data.eal = riskData.results.eal;
    }
  } catch (_) {}
  try {
    const corrRes = await fetch(`${API_BASE}/api/credentials/correlation/status?customer_id=${customerId}`);
    if (corrRes.ok) {
      const corrData = await corrRes.json();
      data.open_findings = corrData.open_findings_by_severity || {};
    }
  } catch (_) {}
  return data;
}

// ─── Prompt builder ───────────────────────────────────────────────────────────

function buildBoardReportPrompt(data) {
  const scores = data.maturity_scores || {};
  const gaps = Object.entries(scores)
    .map(([k, v]) => ({
      label: v.label || k,
      gap: (v.target || 5) - (v.score || 0),
      score: v.score || 0,
      target: v.target || 5,
    }))
    .filter(g => g.gap > 0)
    .sort((a, b) => b.gap - a.gap);

  const avgScore = Object.values(scores).length
    ? (Object.values(scores).reduce((s, v) => s + (v.score || 0), 0) /
        Object.values(scores).length).toFixed(1)
    : "N/A";

  const ealReduction = data.eal_previous
    ? Math.round(((data.eal_previous - data.eal) / data.eal_previous) * 100)
    : null;

  const rem = data.remediation;
  const remPct = rem.total > 0 ? Math.round((rem.complete / rem.total) * 100) : 0;

  return `You are a senior cybersecurity advisor preparing a board-level security report for ${data.org_name} for ${data.report_period}. Write a professional, executive-quality report in clear prose — no markdown headers, no bullet points. Write in full paragraphs suitable for a board of directors of a financial institution. Be direct, specific, and authoritative. Avoid jargon where possible; explain technical concepts briefly when necessary.

The report must cover exactly these five sections in order, each as a titled paragraph block:

SECTION 1 — RISK POSTURE SUMMARY
Current Expected Annual Loss (EAL): $${(data.eal / 1000000).toFixed(2)}M${ealReduction ? ` (reduced ${ealReduction}% from prior period)` : ""}. Frameworks in scope: ${(data.frameworks || []).join(", ")}. Summarize the organization's current cybersecurity risk posture in 2-3 sentences. Be candid about risk level.

SECTION 2 — SECURITY MATURITY
Overall average maturity score: ${avgScore}/5. Top gaps: ${gaps.slice(0, 4).map(g => `${g.label} (${g.score}/${g.target})`).join(", ")}. Write 2-3 sentences describing the maturity landscape and what the gaps mean for the organization's risk profile.

SECTION 3 — REMEDIATION PROGRESS  
${rem.total} total remediation tasks identified. ${rem.complete} complete (${remPct}%), ${rem.in_progress} in progress, ${rem.blocked} blocked. Write 2 sentences on progress and what the board should know about blockers or pace.

SECTION 4 — CRITICAL FINDINGS
Top findings: ${(data.critical_findings || []).slice(0, 5).map(f => `${f.title} (${f.severity})`).join("; ")}. Write 2-3 sentences describing the most serious findings and their business implications for a financial institution.

SECTION 5 — REGULATORY COMPLIANCE
Frameworks: ${(data.frameworks || []).join(", ")}. Based on the maturity scores above, write 2-3 sentences on regulatory exposure and compliance posture. Be specific about which frameworks are most at risk given the identified gaps.

End with a BOARD RECOMMENDATION paragraph of 2-3 sentences summarizing the single most important action the board should authorize or fund this quarter.

Format: Use the exact section titles above (e.g. "RISK POSTURE SUMMARY") as plain text labels before each paragraph. No markdown. No bullet points. Professional tone throughout.`;
}

// ─── Section renderer ─────────────────────────────────────────────────────────

function ReportSection({ title, children, accent }) {
  return (
    <div style={{ ...sty.section, borderLeftColor: accent || "#334155" }}>
      <div style={{ ...sty.sectionTitle, color: accent || "#64748b" }}>{title}</div>
      <div style={sty.sectionBody}>{children}</div>
    </div>
  );
}

function parseReportSections(text) {
  const TITLES = [
    "RISK POSTURE SUMMARY",
    "SECURITY MATURITY",
    "REMEDIATION PROGRESS",
    "CRITICAL FINDINGS",
    "REGULATORY COMPLIANCE",
    "BOARD RECOMMENDATION",
  ];
  const sections = [];
  let remaining = text;
  for (let i = 0; i < TITLES.length; i++) {
    const title = TITLES[i];
    const idx = remaining.indexOf(title);
    if (idx === -1) continue;
    const start = idx + title.length;
    const nextIdx = TITLES.slice(i + 1).reduce((best, t) => {
      const pos = remaining.indexOf(t, start);
      return pos !== -1 && (best === -1 || pos < best) ? pos : best;
    }, -1);
    const content = (nextIdx === -1 ? remaining.slice(start) : remaining.slice(start, nextIdx))
      .replace(/^[\s:—\-]+/, "").trim();
    if (content) sections.push({ title, content });
    if (nextIdx !== -1) remaining = remaining.slice(nextIdx);
  }
  if (sections.length === 0 && text.trim()) {
    sections.push({ title: "BOARD REPORT", content: text.trim() });
  }
  return sections;
}

const SECTION_ACCENTS = {
  "RISK POSTURE SUMMARY":   "#ef4444",
  "SECURITY MATURITY":      "#f59e0b",
  "REMEDIATION PROGRESS":   "#38bdf8",
  "CRITICAL FINDINGS":      "#f97316",
  "REGULATORY COMPLIANCE":  "#a78bfa",
  "BOARD RECOMMENDATION":   "#22c55e",
};

// ─── KPI Card ─────────────────────────────────────────────────────────────────

function KPI({ label, value, sub, color }) {
  return (
    <div style={sty.kpi}>
      <div style={{ ...sty.kpiValue, color: color || "#f1f5f9" }}>{value}</div>
      <div style={sty.kpiLabel}>{label}</div>
      {sub && <div style={sty.kpiSub}>{sub}</div>}
    </div>
  );
}

// ─── Maturity Bar ─────────────────────────────────────────────────────────────

function MaturityBar({ label, score, target }) {
  const pct = Math.round((score / 5) * 100);
  const gap = target - score;
  const barColor = gap >= 3 ? "#ef4444" : gap >= 2 ? "#f97316" : gap >= 1 ? "#eab308" : "#22c55e";
  return (
    <div style={sty.matRow}>
      <div style={sty.matLabel}>{label}</div>
      <div style={sty.matBarWrap}>
        <div style={{ ...sty.matBar, width: `${pct}%`, background: barColor }} />
      </div>
      <div style={sty.matScore}>{score}<span style={sty.matTarget}>/{target}</span></div>
    </div>
  );
}

// ─── Main Component ───────────────────────────────────────────────────────────

export default function ReportingDashboard() {
  const { can, authFetch } = useAuth();
  const [data, setData]           = useState(null);
  const [loading, setLoading]     = useState(true);
  const [generating, setGenerating] = useState(false);
  const [reportText, setReportText] = useState("");
  const [sections, setSections]   = useState([]);
  const [error, setError]         = useState(null);
  const [activeTab, setActiveTab] = useState("overview"); // overview | report

  useEffect(() => { loadData(); }, []);

  const loadData = async () => {
    setLoading(true);
    try {
      const customerId = localStorage.getItem("customer_id") || "cust_techcorpin_575148";
      const d = await fetchReportData(customerId);
      setData(d);
    } catch (e) {
      setError(e.message);
    } finally {
      setLoading(false);
    }
  };

  const handleGenerate = async () => {
    if (!data) return;
    setGenerating(true);
    setReportText("");
    setSections([]);
    setActiveTab("report");
    setError(null);

    try {
      const prompt = buildBoardReportPrompt(data);
      const response = await authFetch(`/api/reports/generate`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ prompt }),
      });

      if (!response.ok) {
        const err = await response.json().catch(() => ({}));
        throw new Error(err.detail || `API error ${response.status}`);
      }

      const result = await response.json();
      const text = result.content || "";
      setReportText(text);
      setSections(parseReportSections(text));
    } catch (e) {
      setError(`Report generation failed: ${e.message}`);
    } finally {
      setGenerating(false);
    }
  };

  if (loading) return (
    <div style={{ ...sty.page, display: "flex", alignItems: "center", justifyContent: "center", color: "#475569" }}>
      Loading report data...
    </div>
  );

  const d = data || MOCK_DATA;
  const scores = d.maturity_scores || {};
  const avgScore = Object.values(scores).length
    ? (Object.values(scores).reduce((s, v) => s + (v.score || 0), 0) / Object.values(scores).length).toFixed(1)
    : "N/A";
  const ealReduction = d.eal_previous
    ? Math.round(((d.eal_previous - d.eal) / d.eal_previous) * 100)
    : null;
  const rem = d.remediation || { total: 0, complete: 0, in_progress: 0, blocked: 0 };
  const remPct = rem.total > 0 ? Math.round((rem.complete / rem.total) * 100) : 0;

  return (
    <div style={sty.page}>
      {/* ── Header ── */}
      <div style={sty.header}>
        <div style={sty.headerLeft}>
          <h1 style={sty.title}>Executive Reports</h1>
          <p style={sty.subtitle}>Board-ready cybersecurity reporting for {d.org_name}</p>
        </div>
        <div style={sty.headerRight}>
          <div style={sty.periodBadge}>{d.report_period}</div>
          {can('export_reports') && (
          <button
            style={{ ...sty.generateBtn, opacity: generating ? 0.7 : 1, cursor: generating ? "not-allowed" : "pointer" }}
            onClick={handleGenerate}
            disabled={generating}
          >
            {generating ? "⟳ Generating..." : "✦ Generate Board Report"}
          </button>
          )}
        </div>
      </div>

      {/* ── Tab Bar ── */}
      <div style={sty.tabBar}>
        {[["overview", "📊 Data Overview"], ["report", "📄 Board Report"]].map(([t, label]) => (
          <button key={t}
            style={{ ...sty.tab, ...(activeTab === t ? sty.tabActive : {}) }}
            onClick={() => setActiveTab(t)}>
            {label}
            {t === "report" && sections.length > 0 && (
              <span style={sty.tabPill}>Ready</span>
            )}
          </button>
        ))}
      </div>

      {/* ── Content ── */}
      <div style={sty.content}>
        {error && (
          <div style={sty.errorBanner}>⚠ {error}</div>
        )}

        {activeTab === "overview" && (
          <>
            {/* KPI Strip */}
            <div style={sty.kpiStrip}>
              <KPI label="Expected Annual Loss"  value={`$${(d.eal/1000000).toFixed(2)}M`}
                sub={ealReduction ? `↓ ${ealReduction}% from prior period` : null} color="#ef4444" />
              <KPI label="Avg Maturity Score"    value={`${avgScore}/5`}  color="#f59e0b" />
              <KPI label="Tasks Complete"        value={`${rem.complete}/${rem.total}`}
                sub={`${remPct}% complete`} color="#38bdf8" />
              <KPI label="Blocked Tasks"         value={rem.blocked}      color={rem.blocked > 0 ? "#f97316" : "#22c55e"} />
              <KPI label="Frameworks in Scope"   value={(d.frameworks||[]).join(", ")} color="#a78bfa" />
            </div>

            {/* Two-column: Maturity + Findings */}
            <div style={sty.twoCol}>
              {/* Maturity Scores */}
              <div style={sty.card}>
                <div style={sty.cardTitle}>Security Maturity by Category</div>
                <div style={sty.cardSub}>Current score vs. target level</div>
                <div style={{ marginTop: 16, display: "flex", flexDirection: "column", gap: 10 }}>
                  {Object.entries(scores).map(([key, val]) => (
                    <MaturityBar
                      key={key}
                      label={val.label || key}
                      score={val.score || 0}
                      target={val.target || 5}
                    />
                  ))}
                </div>
              </div>

              {/* Critical Findings */}
              <div style={sty.card}>
                <div style={sty.cardTitle}>Top Critical Findings</div>
                <div style={sty.cardSub}>Highest priority items for board attention</div>
                <div style={{ marginTop: 16, display: "flex", flexDirection: "column", gap: 8 }}>
                  {(d.critical_findings || []).map((f, i) => {
                    const sc = SEV_CONFIG[f.severity] || SEV_CONFIG.medium;
                    return (
                      <div key={i} style={sty.findingRow}>
                        <span style={{ ...sty.sevPill, background: sc.bg, color: sc.color }}>
                          {sc.label}
                        </span>
                        <div style={{ flex: 1, minWidth: 0 }}>
                          <div style={sty.findingTitle}>{f.title}</div>
                          <div style={sty.findingCat}>{f.category}</div>
                        </div>
                      </div>
                    );
                  })}
                </div>

                {/* Remediation progress bar */}
                <div style={{ marginTop: 24 }}>
                  <div style={sty.cardTitle}>Remediation Progress</div>
                  <div style={sty.remBar}>
                    <div style={{ ...sty.remFill, width: `${remPct}%` }} />
                  </div>
                  <div style={sty.remLegend}>
                    <span style={{ color: "#22c55e" }}>{rem.complete} complete</span>
                    <span style={{ color: "#38bdf8" }}>{rem.in_progress} in progress</span>
                    <span style={{ color: "#f97316" }}>{rem.blocked} blocked</span>
                  </div>
                </div>
              </div>
            </div>

            {/* Generate CTA */}
            <div style={sty.ctaCard}>
              <div style={sty.ctaLeft}>
                <div style={sty.ctaTitle}>Ready to generate your board report?</div>
                <div style={sty.ctaSub}>
                  Claude will synthesize all data above into a professional narrative
                  suitable for your board of directors and executive leadership.
                </div>
              </div>
              <button
                style={{ ...sty.generateBtn, ...(generating ? { opacity: 0.7, cursor: "not-allowed" } : {}) }}
                onClick={handleGenerate}
                disabled={generating}
              >
                {generating ? "⟳ Generating..." : "✦ Generate Board Report"}
              </button>
            </div>
          </>
        )}

        {activeTab === "report" && (
          <div style={sty.reportPane}>
            {generating && (
              <div style={sty.generatingState}>
                <div style={sty.generatingIcon}>✦</div>
                <div style={sty.generatingText}>
                  Claude is drafting your board report...
                </div>
                <div style={sty.generatingHint}>
                  Synthesizing EAL data, maturity scores, remediation progress, and regulatory posture
                </div>
              </div>
            )}

            {!generating && sections.length === 0 && !error && (
              <div style={sty.emptyReport}>
                <div style={sty.emptyIcon}>📄</div>
                <div style={sty.emptyTitle}>No report generated yet</div>
                <div style={sty.emptySub}>Click "Generate Board Report" to create your executive report</div>
                <button style={sty.generateBtn} onClick={handleGenerate}>
                  ✦ Generate Board Report
                </button>
              </div>
            )}

            {sections.length > 0 && (
              <>
                {/* Report Header */}
                <div style={sty.reportHeader}>
                  <div style={sty.reportOrg}>{d.org_name}</div>
                  <div style={sty.reportTitle}>Cybersecurity Board Report</div>
                  <div style={sty.reportMeta}>
                    {d.report_period} · Generated {new Date().toLocaleDateString("en-US", {
                      month: "long", day: "numeric", year: "numeric"
                    })} · Prepared by CISO Advisor
                  </div>
                  <div style={sty.reportDivider} />
                </div>

                {/* Sections */}
                {sections.map((s, i) => (
                  <ReportSection
                    key={i}
                    title={s.title}
                    accent={SECTION_ACCENTS[s.title]}
                  >
                    {s.content}
                  </ReportSection>
                ))}

                {/* Footer */}
                <div style={sty.reportFooter}>
                  <div style={sty.footerConfidential}>CONFIDENTIAL — FOR BOARD USE ONLY</div>
                  <div style={sty.footerSub}>
                    Generated by CISO Advisor · Moyer Scientific Research · {new Date().toLocaleDateString()}
                  </div>
                </div>
              </>
            )}
          </div>
        )}
      </div>
    </div>
  );
}

// ─── Styles ───────────────────────────────────────────────────────────────────

const sty = {
  page: {
    minHeight: "100vh",
    background: "#060810",
    color: "#e2e8f0",
    fontFamily: "'Libre Baskerville', 'Georgia', serif",
  },
  header: {
    display: "flex", justifyContent: "space-between", alignItems: "flex-start",
    padding: "30px 40px 22px", borderBottom: "1px solid #111827",
  },
  headerLeft: { display: "flex", flexDirection: "column", gap: 5 },
  headerRight: { display: "flex", alignItems: "center", gap: 12 },
  title: {
    fontSize: 26, fontWeight: 700, color: "#f1f5f9",
    margin: 0, letterSpacing: "-0.02em",
  },
  subtitle: { fontSize: 13, color: "#475569", margin: 0 },
  periodBadge: {
    fontSize: 12, color: "#64748b",
    background: "#111827", border: "1px solid #1f2937",
    borderRadius: 6, padding: "6px 12px",
    fontFamily: "'DM Mono', monospace",
  },
  generateBtn: {
    padding: "11px 22px",
    background: "linear-gradient(135deg, #1e3a5f, #1e40af)",
    border: "1px solid #1d4ed860",
    borderRadius: 9, color: "#93c5fd",
    fontSize: 14, fontWeight: 700, cursor: "pointer",
    letterSpacing: "0.02em",
    boxShadow: "0 4px 16px #1e40af20",
  },
  tabBar: {
    display: "flex", borderBottom: "1px solid #111827",
    padding: "0 40px", background: "#06080d",
  },
  tab: {
    padding: "13px 20px", background: "none",
    border: "none",
    borderBottomWidth: "2px", borderBottomStyle: "solid", borderBottomColor: "transparent",
    color: "#475569", fontSize: 13, fontWeight: 500,
    cursor: "pointer", display: "flex", alignItems: "center", gap: 8,
    fontFamily: "'DM Mono', monospace",
  },
  tabActive: { color: "#93c5fd", borderBottomColor: "#1d4ed8" },
  tabPill: {
    fontSize: 9, fontWeight: 700, letterSpacing: "0.06em",
    background: "#22c55e20", color: "#22c55e",
    border: "1px solid #22c55e30", borderRadius: 10,
    padding: "1px 7px",
  },
  content: { padding: "28px 40px", display: "flex", flexDirection: "column", gap: 20 },
  errorBanner: {
    background: "#1a0c0c", border: "1px solid #ef444430",
    borderRadius: 8, padding: "12px 16px",
    fontSize: 13, color: "#ef4444",
  },
  // KPI
  kpiStrip: {
    display: "flex", gap: 0,
    background: "#0a0d14", border: "1px solid #111827",
    borderRadius: 12, overflow: "hidden",
  },
  kpi: {
    flex: 1, padding: "18px 20px",
    borderRight: "1px solid #111827",
    display: "flex", flexDirection: "column", gap: 4,
  },
  kpiValue: { fontSize: 20, fontWeight: 700, lineHeight: 1 },
  kpiLabel: {
    fontSize: 10, color: "#334155",
    letterSpacing: "0.06em", textTransform: "uppercase",
    fontFamily: "'DM Mono', monospace",
  },
  kpiSub: { fontSize: 11, color: "#475569", marginTop: 2 },
  // Two col
  twoCol: { display: "grid", gridTemplateColumns: "1fr 1fr", gap: 20 },
  card: {
    background: "#0c0f1a", border: "1px solid #111827",
    borderRadius: 12, padding: "22px 24px",
  },
  cardTitle: { fontSize: 14, fontWeight: 700, color: "#f1f5f9" },
  cardSub: { fontSize: 12, color: "#475569", marginTop: 3 },
  // Maturity
  matRow: { display: "flex", alignItems: "center", gap: 10 },
  matLabel: { fontSize: 11, color: "#64748b", width: 160, flexShrink: 0, fontFamily: "'DM Mono', monospace" },
  matBarWrap: { flex: 1, height: 6, background: "#111827", borderRadius: 3, overflow: "hidden" },
  matBar: { height: "100%", borderRadius: 3, transition: "width 0.5s ease" },
  matScore: { fontSize: 12, color: "#94a3b8", width: 30, textAlign: "right", fontFamily: "'DM Mono', monospace" },
  matTarget: { color: "#334155" },
  // Findings
  findingRow: { display: "flex", gap: 10, alignItems: "flex-start" },
  sevPill: {
    fontSize: 10, fontWeight: 700, borderRadius: 4,
    padding: "2px 7px", flexShrink: 0,
    fontFamily: "'DM Mono', monospace",
  },
  findingTitle: { fontSize: 12, color: "#94a3b8", lineHeight: 1.4 },
  findingCat: { fontSize: 10, color: "#334155", marginTop: 2 },
  // Remediation
  remBar: {
    height: 8, background: "#111827", borderRadius: 4,
    overflow: "hidden", marginTop: 10,
  },
  remFill: {
    height: "100%", borderRadius: 4,
    background: "linear-gradient(90deg, #1d4ed8, #22c55e)",
  },
  remLegend: {
    display: "flex", gap: 16, marginTop: 8,
    fontSize: 11, fontFamily: "'DM Mono', monospace",
  },
  // CTA
  ctaCard: {
    display: "flex", justifyContent: "space-between", alignItems: "center",
    background: "linear-gradient(135deg, #0c1220, #0f172a)",
    border: "1px solid #1d4ed830",
    borderRadius: 12, padding: "24px 28px", gap: 20,
  },
  ctaLeft: { display: "flex", flexDirection: "column", gap: 5 },
  ctaTitle: { fontSize: 15, fontWeight: 700, color: "#f1f5f9" },
  ctaSub: { fontSize: 13, color: "#475569", maxWidth: 520, lineHeight: 1.6 },
  // Report pane
  reportPane: {
    background: "#0c0f1a", border: "1px solid #111827",
    borderRadius: 12, padding: "40px 48px",
    maxWidth: 820, margin: "0 auto", width: "100%",
  },
  generatingState: {
    display: "flex", flexDirection: "column", alignItems: "center",
    padding: "60px 0", gap: 14,
  },
  generatingIcon: {
    fontSize: 36, color: "#1d4ed8",
    animation: "spin 2s linear infinite",
  },
  generatingText: { fontSize: 16, color: "#94a3b8", fontWeight: 600 },
  generatingHint: { fontSize: 13, color: "#334155", textAlign: "center", maxWidth: 400 },
  emptyReport: {
    display: "flex", flexDirection: "column", alignItems: "center",
    padding: "60px 0", gap: 12,
  },
  emptyIcon: { fontSize: 40, opacity: 0.3 },
  emptyTitle: { fontSize: 16, fontWeight: 600, color: "#64748b" },
  emptySub: { fontSize: 13, color: "#334155", textAlign: "center" },
  // Report document
  reportHeader: { marginBottom: 36, textAlign: "center" },
  reportOrg: {
    fontSize: 12, color: "#475569", letterSpacing: "0.12em",
    textTransform: "uppercase", marginBottom: 8,
    fontFamily: "'DM Mono', monospace",
  },
  reportTitle: {
    fontSize: 28, fontWeight: 700, color: "#f1f5f9",
    letterSpacing: "-0.02em", marginBottom: 10,
  },
  reportMeta: { fontSize: 12, color: "#334155", fontFamily: "'DM Mono', monospace" },
  reportDivider: {
    height: 1, background: "linear-gradient(90deg, transparent, #1d4ed840, transparent)",
    marginTop: 24,
  },
  section: {
    borderLeft: "3px solid",
    paddingLeft: 20, marginBottom: 28,
  },
  sectionTitle: {
    fontSize: 10, fontWeight: 700, letterSpacing: "0.14em",
    textTransform: "uppercase", marginBottom: 10,
    fontFamily: "'DM Mono', monospace",
  },
  sectionBody: {
    fontSize: 15, color: "#94a3b8", lineHeight: 1.8,
    fontStyle: "normal",
  },
  reportFooter: {
    marginTop: 48, paddingTop: 20,
    borderTop: "1px solid #111827",
    textAlign: "center", display: "flex", flexDirection: "column", gap: 5,
  },
  footerConfidential: {
    fontSize: 10, fontWeight: 700, letterSpacing: "0.14em",
    color: "#334155", fontFamily: "'DM Mono', monospace",
  },
  footerSub: { fontSize: 11, color: "#1f2937", fontFamily: "'DM Mono', monospace" },
};
